import * as THREE from "three";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

/**
 * Blazor JS-interop bridge for the 3D symbols, rendered with Three.js. They are 3D re-imaginings
 * of the older CSS symbols:
 *
 *  - sparkly-3d : a swirling galaxy-like orb of thousands of glowing particles (was SparklySymbol)
 *  - driply-3d  : a glossy liquid blob that wobbles and sheds droplets (was DriplySymbol)
 *  - orbitz-3d  : nested translucent glass shells with flowing contour lines and orbiting
 *                 beads (was OrbitzSymbol)
 *
 * All three tilt toward the cursor. Everything is GPU-driven (vertex-shader noise), so the cost
 * is low even with many particles.
 *
 * Exposed on window.App as:
 *  - App.symbols3dInit(elementId, options)    mount a Three.js canvas into the container div
 *  - App.symbols3dUpdate(elementId, options)  update options live (rebuilds on variant / particle count change)
 *  - App.symbols3dDispose(elementId)          stop the loop and free WebGL resources
 *
 * Called from Symbol3D.razor.cs.
 */

export interface Symbol3DInteropOptions {
    variant?: string;      // sparkly-3d | driply-3d | orbitz-3d | crystal-3d | glossy-3d
    color?: string;        // hex, primary color
    color2?: string;       // hex, accent color
    bgColor?: string;      // hex
    speed?: number;        // animation speed multiplier
    scale?: number;        // zoom
    intensity?: number;    // glow / brightness
    distortion?: number;   // how strongly the blob / shells wobble (driply, orbitz)
    particles?: number;    // particle count (sparkly)
    tilt?: number;         // how strongly the symbol follows the cursor (0 = off)
    mode?: string;         // idle | listening | talking | thinking
}

/** Smoothed mix of the four modes (the weights always sum to 1, so a switch cross-fades). */
interface ModeWeights { idle: number; listen: number; talk: number; think: number }

interface Built {
    root: THREE.Object3D;
    update(t: number, dt: number, o: Symbol3DInteropOptions, w: ModeWeights, speech: number): void;
    resize?(height: number, pixelRatio: number): void;
}

interface Mount3D {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    pivot: THREE.Group;
    container: HTMLElement;
    observer: ResizeObserver;
    raf: number;
    options: Symbol3DInteropOptions;
    built?: Built;
    envRT?: THREE.WebGLRenderTarget;   // studio reflections, only while a glass symbol is shown
    envKind?: string;                  // which studio backdrop envRT holds ("" = none)
    time: number;
    lastFrame: number;
    pointer: { x: number; y: number; active: boolean };
    tilt: { x: number; y: number };
    w: ModeWeights;
    onMove: (e: PointerEvent) => void;
    onLeave: () => void;
}

const mounts = new Map<string, Mount3D>();

const CAMERA_Z = 7.5;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const rand = (lo: number, hi: number): number => lo + Math.random() * (hi - lo);

/**
 * A synthetic "voice" envelope (0..1) for the talking mode: syllable-like pulses grouped into
 * phrases with short pauses between them, so it reads as speech without needing real audio.
 */
function speechEnv(t: number): number {
    const syllable = Math.pow(Math.max(0, Math.sin(t * 9 + Math.sin(t * 2.3) * 2)), 1.3);
    const phrase = clamp((Math.sin(t * 0.9 + Math.sin(t * 0.37) * 2) + 0.25) * 2.5, 0, 1);
    return clamp(syllable * (0.45 + 0.55 * Math.sin(t * 5.1 + 1) * Math.sin(t * 5.1 + 1)) * phrase * 1.15, 0, 1);
}

const MODES = ["idle", "listening", "talking", "thinking"] as const;

/** A THREE.Color that is only re-parsed when its hex string changes. */
class Hex {
    readonly c = new THREE.Color();
    private h = "";
    set(value: string): THREE.Color {
        if (value !== this.h) {
            this.h = value;
            this.c.set(value);
        }
        return this.c;
    }
}

// ---------- shared GLSL ----------

// Ashima Arts / Stefan Gustavson 3D simplex noise (MIT).
const NOISE_GLSL = /* glsl */ `
vec3 mod289(vec3 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 mod289(vec4 x){return x-floor(x*(1.0/289.0))*289.0;}
vec4 permute(vec4 x){return mod289(((x*34.0)+1.0)*x);}
vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}
float snoise(vec3 v){
  const vec2 C=vec2(1.0/6.0,1.0/3.0);
  const vec4 D=vec4(0.0,0.5,1.0,2.0);
  vec3 i=floor(v+dot(v,C.yyy));
  vec3 x0=v-i+dot(i,C.xxx);
  vec3 g=step(x0.yzx,x0.xyz);
  vec3 l=1.0-g;
  vec3 i1=min(g.xyz,l.zxy);
  vec3 i2=max(g.xyz,l.zxy);
  vec3 x1=x0-i1+C.xxx;
  vec3 x2=x0-i2+C.yyy;
  vec3 x3=x0-D.yyy;
  i=mod289(i);
  vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));
  float n_=0.142857142857;
  vec3 ns=n_*D.wyz-D.xzx;
  vec4 j=p-49.0*floor(p*ns.z*ns.z);
  vec4 x_=floor(j*ns.z);
  vec4 y_=floor(j-7.0*x_);
  vec4 x=x_*ns.x+ns.yyyy;
  vec4 y=y_*ns.x+ns.yyyy;
  vec4 h=1.0-abs(x)-abs(y);
  vec4 b0=vec4(x.xy,y.xy);
  vec4 b1=vec4(x.zw,y.zw);
  vec4 s0=floor(b0)*2.0+1.0;
  vec4 s1=floor(b1)*2.0+1.0;
  vec4 sh=-step(h,vec4(0.0));
  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy;
  vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;
  vec3 p0=vec3(a0.xy,h.x);
  vec3 p1=vec3(a0.zw,h.y);
  vec3 p2=vec3(a1.xy,h.z);
  vec3 p3=vec3(a1.zw,h.w);
  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));
  p0*=norm.x;p1*=norm.y;p2*=norm.z;p3*=norm.w;
  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0);
  m=m*m;
  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));
}
`;

const OUTPUT_GLSL = /* glsl */ `
#include <tonemapping_fragment>
#include <colorspace_fragment>
`;

/**
 * Vertex shader for a sphere whose surface is displaced by `displace(dir)` (GLSL, supplied by the
 * caller). Normals come from finite differences of the displaced surface, so lighting stays correct.
 */
function surfaceVertex(displaceGlsl: string, uniformsGlsl: string): string {
    return /* glsl */ `
${NOISE_GLSL}
uniform float uTime;
${uniformsGlsl}
varying vec3 vN;
varying vec3 vV;
varying vec3 vD;
varying float vNoise;
${displaceGlsl}
void main() {
  vec3 d = normalize(position);
  vec3 tA = normalize(abs(d.y) < 0.99 ? cross(d, vec3(0.0, 1.0, 0.0)) : cross(d, vec3(1.0, 0.0, 0.0)));
  vec3 tB = cross(d, tA);
  float e = 0.02;
  vec3 p = displace(d);
  vec3 pA = displace(normalize(d + tA * e));
  vec3 pB = displace(normalize(d + tB * e));
  vec3 n = normalize(cross(pA - p, pB - p));
  if (dot(n, d) < 0.0) n = -n;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  vN = normalize(normalMatrix * n);
  vV = -mv.xyz;
  vD = d;
  gl_Position = projectionMatrix * mv;
}
`;
}

// ---------- shared helpers ----------

function glowTexture(): THREE.CanvasTexture {
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d")!;
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.25, "rgba(255,255,255,0.45)");
    g.addColorStop(0.6, "rgba(255,255,255,0.1)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

function glowSprite(map: THREE.Texture): THREE.Sprite {
    const mat = new THREE.SpriteMaterial({
        map, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
    });
    return new THREE.Sprite(mat);
}

/**
 * A photo studio made of big, soft softboxes. Their colours go above 1, so the reflections stay strong
 * through the filmic tone mapping — unlike a canvas backdrop, which can never be brighter than white.
 * Smooth light instead of the default room's hard-edged panels means the water ripples bend the
 * reflections into gentle bands rather than splintering them into jagged blocks.
 */
function softStudioScene(tint: THREE.Color): { scene: THREE.Scene; dispose: () => void } {
    const scene = new THREE.Scene();
    const disposables: { dispose: () => void }[] = [];
    const light = tint.clone().lerp(new THREE.Color(1, 1, 1), 0.25);   // the accent colour, lifted just enough to read as light
    // saturated colours carry less luminance, so lift the studio to keep the glass equally bright at any hue
    const lum = clamp(0.2126 * light.r + 0.7152 * light.g + 0.0722 * light.b, 0.25, 1);
    const gain = Math.min(2.2, 1 / lum);

    // the dark room itself, seen from the inside, faintly tinted so the shadowed side picks up the colour
    const roomGeo = new THREE.SphereGeometry(30, 32, 24);
    const roomMat = new THREE.MeshBasicMaterial({
        color: tint.clone().multiplyScalar(0.028),
        side: THREE.BackSide,
    });
    scene.add(new THREE.Mesh(roomGeo, roomMat));
    disposables.push(roomGeo, roomMat);

    const addLight = (map: THREE.Texture, w: number, h: number, power: number, x: number, y: number, z: number): void => {
        const geo = new THREE.PlaneGeometry(w, h);
        const mat = new THREE.MeshBasicMaterial({
            map, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
            color: light.clone().multiplyScalar(power * gain),
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(x, y, z);
        mesh.lookAt(0, 0, 0);
        scene.add(mesh);
        disposables.push(geo, mat);
    };

    const soft = glowTexture();
    const strip = stripTexture();
    disposables.push(soft, strip);

    addLight(soft, 12, 12, 2.4, -7, 9, 7);    // key, high on the left
    addLight(strip, 6, 20, 5, 10, 0, 3);      // strip light on the right — the bright edge that reads as glass
    addLight(soft, 18, 18, 2.2, 9, 3, -6);    // cool fill behind on the right
    addLight(soft, 26, 26, 1.1, 0, -12, 0);   // floor bounce, so the lower half is not pitch black

    return {
        scene,
        dispose: () => { for (const d of disposables) d.dispose(); },
    };
}

/** A long, soft bar of light, used for the studio's strip light. */
function stripTexture(): THREE.CanvasTexture {
    const size = 128;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext("2d")!;

    const across = ctx.createLinearGradient(0, 0, size, 0);
    across.addColorStop(0, "rgba(255,255,255,0)");
    across.addColorStop(0.5, "rgba(255,255,255,1)");
    across.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = across;
    ctx.fillRect(0, 0, size, size);

    const ends = ctx.createLinearGradient(0, 0, 0, size);
    ends.addColorStop(0, "rgba(0,0,0,1)");
    ends.addColorStop(0.3, "rgba(0,0,0,0)");
    ends.addColorStop(0.7, "rgba(0,0,0,0)");
    ends.addColorStop(1, "rgba(0,0,0,1)");
    ctx.globalCompositeOperation = "destination-out";
    ctx.fillStyle = ends;
    ctx.fillRect(0, 0, size, size);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
}

function disposeObject(root: THREE.Object3D): void {
    root.traverse((obj) => {
        const m = obj as THREE.Mesh;
        m.geometry?.dispose();
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        for (const x of Array.isArray(mat) ? mat : mat ? [mat] : []) {
            (x as THREE.SpriteMaterial).map?.dispose();
            x.dispose();
        }
    });
}

// ---------- plasma shell ----------

/**
 * A luminous, translucent "energy orb" shell: a bright rim, fine grainy cellular texture, and
 * concentric ripples that converge on the middle of the sphere as if you were looking down a
 * glowing tunnel. Used by all three symbols (alone, layered, or as a haze inside the particles).
 */
const PLASMA_FRAGMENT = /* glsl */ `
${NOISE_GLSL}
uniform vec3 uDeep;
uniform vec3 uMid;
uniform vec3 uHot;
uniform float uAlpha;
uniform float uIntensity;
uniform float uRipple;
uniform float uSeed;
uniform float uTime;
uniform vec3 uPulseDir;      // where the pulse starts (object space)
uniform float uPulseAge;     // 0..1 while a pulse is travelling, < 0 when none
varying vec3 vN;
varying vec3 vV;
varying vec3 vD;
varying float vNoise;
void main() {
  vec3 N = normalize(vN);
  vec3 V = normalize(vV);
  float edge = 1.0 - abs(dot(N, V));                       // 0 at the centre, 1 on the silhouette
  float rim = pow(edge, 1.7);
  vec3 q = vD * 2.2 + vec3(uSeed, uSeed * 0.7, uSeed * 1.3);
  float warp = snoise(q + vec3(0.0, uTime * 0.18, 0.0));
  float rip = 0.5 + 0.5 * sin(edge * uRipple + warp * 2.4 - uTime * 1.1);   // converging ripples
  float g = snoise(vD * 9.0 + warp * 1.2 + vec3(0.0, -uTime * 0.35, uSeed));
  float grain = 1.0 - abs(g);
  grain = grain * grain * grain;                           // thin bright cell walls
  float k = 0.34 + 0.85 * rim + 0.3 * rip * (0.35 + edge) + 0.5 * grain * (0.25 + rim);
  k *= gl_FrontFacing ? 1.0 : 0.7;
  vec3 L = normalize(vec3(-0.45, 0.6, 0.65));
  float lit = clamp(dot(N, L) * 0.5 + 0.5, 0.0, 1.0);
  k *= mix(0.8, 1.25, lit * lit);                          // lit from the upper left, shadowed lower right
  float spec = pow(max(dot(N, normalize(L + V)), 0.0), 70.0);
  if (uPulseAge >= 0.0) {                                  // a magic pulse: a ring of light sweeping over the surface
    float pang = acos(clamp(dot(normalize(vD), uPulseDir), -1.0, 1.0));
    float pd = pang - uPulseAge * 3.4;
    k += exp(-pd * pd * 16.0) * (1.0 - uPulseAge) * 1.1;
  }
  k = clamp(k, 0.0, 1.7);
  vec3 col = mix(uDeep, uMid, smoothstep(0.1, 0.65, k));
  col = mix(col, uHot, smoothstep(0.75, 1.6, k));
  float a = clamp(k * uAlpha, 0.0, 1.0);
  col += vec3(1.0) * spec * 0.5;                           // a soft highlight, so it reads as a glossy ball
  gl_FragColor = vec4(col * a * uIntensity, a);
${OUTPUT_GLSL}
}`;

interface PlasmaOptions {
    radius: number;
    amp: number;       // silhouette wobble
    freq: number;      // wobble frequency
    seed: number;
    ripple: number;    // ripple count toward the centre
    alpha: number;
}

function plasmaShell(p: PlasmaOptions): THREE.Mesh {
    const mat = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uAmp: { value: p.amp },
            uSeed: { value: p.seed },
            uFreq: { value: p.freq },
            uRipple: { value: p.ripple },
            uAlpha: { value: p.alpha },
            uIntensity: { value: 1 },
            uDeep: { value: new THREE.Color() },
            uMid: { value: new THREE.Color() },
            uHot: { value: new THREE.Color() },
            uPulseDir: { value: new THREE.Vector3(0, 1, 0) },
            uPulseAge: { value: -1 },
        },
        transparent: true,
        depthWrite: false,
        side: THREE.FrontSide,
        blending: THREE.AdditiveBlending,
        vertexShader: surfaceVertex(/* glsl */ `
vec3 displace(vec3 d) {
  float n = snoise(d * uFreq + vec3(uSeed, uTime * 0.3, uSeed * 0.5));
  vNoise = n;
  return d * (1.0 + uAmp * n);
}`, "uniform float uAmp;\nuniform float uSeed;\nuniform float uFreq;"),
        fragmentShader: PLASMA_FRAGMENT,
    });
    const mesh = new THREE.Mesh(new THREE.IcosahedronGeometry(1, 36), mat);
    mesh.scale.setScalar(p.radius);
    return mesh;
}

const hslScratch = { h: 0, s: 0, l: 0 };

/**
 * Fills the three plasma colors (deep interior, mid body, hot rim) from one color. The rim defaults to
 * a pale tint of the body color; pass `rim` to give the edge its own color instead. A rim color is kept
 * at a mid brightness so the glow stays colored instead of burning out to white.
 */
function setPlasmaPalette(mesh: THREE.Mesh, source: THREE.Color, rim?: THREE.Color): void {
    const u = (mesh.material as THREE.ShaderMaterial).uniforms;
    source.getHSL(hslScratch, THREE.SRGBColorSpace);
    const { h, l } = hslScratch;
    const s = Math.max(hslScratch.s, 0.6);
    (u.uDeep.value as THREE.Color).setHSL(h, s, clamp(l * 0.55, 0.2, 0.38), THREE.SRGBColorSpace);
    (u.uMid.value as THREE.Color).setHSL(h, s, clamp(l, 0.4, 0.62), THREE.SRGBColorSpace);
    if (rim) {
        rim.getHSL(hslScratch, THREE.SRGBColorSpace);
        (u.uHot.value as THREE.Color).setHSL(hslScratch.h, Math.max(hslScratch.s, 0.6), clamp(hslScratch.l, 0.4, 0.55), THREE.SRGBColorSpace);
    } else {
        (u.uHot.value as THREE.Color).setHSL((h + 0.97) % 1, s * 0.75, 0.82, THREE.SRGBColorSpace);
    }
}

function setPlasmaUniforms(mesh: THREE.Mesh, t: number, intensity: number, amp?: number): void {
    const u = (mesh.material as THREE.ShaderMaterial).uniforms;
    u.uTime.value = t;
    u.uIntensity.value = intensity;
    if (amp !== undefined) u.uAmp.value = amp;
}

// ---------- Sparkly 3D ----------

/**
 * Same idea as the CSS SparklySymbol: a few hundred crisp dots in one hue family. Here every dot
 * is a lit 3D sphere with a soft halo, and each mode has its own gesture:
 *  - idle      a loose, gently breathing ball with a little depth spread
 *  - listening the dots spread through the volume like the original: they fade in at the centre,
 *              settle, then fly outward and fade
 *  - talking   every syllable pushes the cloud outward, each dot by a different amount
 *  - thinking  the dots draw into a tight glowing helix that twists and spins fast
 */
function buildSparkly(o: Symbol3DInteropOptions): Built {
    const n = clamp(Math.round(o.particles ?? 400), 100, 4000);
    const dirs = new Float32Array(n * 3);
    const seeds = new Float32Array(n * 4);
    for (let i = 0; i < n; i++) {
        const u = rand(-1, 1);
        const ph = rand(0, Math.PI * 2);
        const s = Math.sqrt(1 - u * u);
        dirs.set([s * Math.cos(ph), u, s * Math.sin(ph)], i * 3);
        seeds.set([rand(-1, 1), Math.random(), Math.random(), rand(0.8, 1.35)], i * 4);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(dirs, 3));
    geo.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 4));

    const hsl = { h: 0, s: 0, l: 0 };
    const uniforms = {
        uTime: { value: 0 },
        uSize: { value: 60 },
        uPixel: { value: 1 },
        uIntensity: { value: 1 },
        uHsl: { value: new THREE.Vector3(0.6, 1, 0.55) },
        uW: { value: new THREE.Vector4(1, 0, 0, 0) },
        uSpeech: { value: 0 },
    };
    const mat = new THREE.ShaderMaterial({
        uniforms,
        transparent: true,
        depthWrite: false,
        vertexShader: /* glsl */ `
attribute vec4 aSeed;
uniform float uTime, uSize, uPixel, uSpeech;
uniform vec4 uW;
uniform vec3 uHsl;
varying vec3 vCol;
varying float vA;
varying float vShine;
vec3 hsl2rgb(vec3 c) {
  vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
  return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
}
void main() {
  float c = fract(uTime / 9.0 + aSeed.y);
  float cycleA = smoothstep(0.0, 0.2, c) * (1.0 - smoothstep(0.82, 1.0, c));
  float cycleR = mix(0.15, 1.0, smoothstep(0.0, 0.3, c)) + 1.7 * smoothstep(0.72, 1.0, c);
  float idleR = (0.8 + 0.4 * aSeed.z) + 0.05 * sin(uTime * 1.2 + aSeed.z * 6.2831);
  float talkR = (0.95 + 0.25 * aSeed.z)
              + uSpeech * (0.25 + 0.7 * aSeed.y) * (0.6 + 0.4 * sin(uTime * 7.0 + aSeed.z * 6.2831));
  float thinkR = 0.62 + 0.14 * sin(uTime * 2.0 + aSeed.z * 6.2831) + 0.1 * aSeed.y;
  float r = idleR * uW.x + cycleR * uW.y + talkR * uW.z + thinkR * uW.w;
  vA = 0.85 * uW.x + cycleA * uW.y + 0.95 * uW.z + 0.95 * uW.w;

  vec3 d = position;
  float tw = uW.w * (uTime * 2.2 + d.y * 5.0);                // helix twist while thinking
  d.xz = mat2(cos(tw), -sin(tw), sin(tw), cos(tw)) * d.xz;
  vec4 mv = modelViewMatrix * vec4(d * r * 1.45, 1.0);
  gl_Position = projectionMatrix * mv;
  float far = clamp((-mv.z - 5.6) / 3.6, 0.0, 1.0);          // 0 = near the camera, 1 = far side
  float awake = 1.0 - uW.x;
  // glints: now and then a dot flashes up with a star, the near ones the most
  float glint = pow(max(sin(uTime * (0.8 + aSeed.z * 1.6) + aSeed.y * 40.0), 0.0), 20.0);
  vShine = glint * (0.35 + 0.65 * (1.0 - far));
  gl_PointSize = uSize * aSeed.w * uPixel / (-mv.z) * mix(1.7, 0.55, far) * mix(0.85, 1.0, awake)
               * (1.0 + 0.5 * uSpeech * uW.z) * (1.0 + 1.4 * vShine);
  // the hue drifts slowly through the cloud, like an aurora
  float h = fract(uHsl.x + aSeed.x * 0.139 + 0.06 * sin(position.y * 2.5 + uTime * 0.6) + 1.0);
  vCol = hsl2rgb(vec3(h, uHsl.y, uHsl.z)) * mix(1.25, 0.3, far)       // depth: near dots bright, far dots dim
       * (1.0 + 0.5 * uW.w + 0.4 * uSpeech * uW.z) * (1.0 + 1.8 * vShine);
}`,
        fragmentShader: /* glsl */ `
uniform float uIntensity;
varying vec3 vCol;
varying float vA;
varying float vShine;
void main() {
  vec2 p = (gl_PointCoord - 0.5) * 2.0;
  float d = length(p);
  const float R = 0.42;
  if (d > 1.0) discard;
  // a four-point star across the sprite while it glints
  float star = (smoothstep(0.06, 0.0, abs(p.x)) * smoothstep(1.0, 0.0, abs(p.y))
              + smoothstep(0.06, 0.0, abs(p.y)) * smoothstep(1.0, 0.0, abs(p.x))) * vShine;
  vec4 outc;
  if (d < R) {
    vec3 n = vec3(p.x / R, -p.y / R, sqrt(max(0.0, 1.0 - (d / R) * (d / R))));
    vec3 L = normalize(vec3(-0.4, 0.6, 0.7));
    float diff = clamp(dot(n, L), 0.0, 1.0);
    float spec = pow(clamp(dot(reflect(-L, n), vec3(0.0, 0.0, 1.0)), 0.0, 1.0), 20.0);
    vec3 col = vCol * (0.4 + 0.8 * diff) + spec * 0.55;
    outc = vec4(col * uIntensity, vA * smoothstep(R, R * 0.85, d));
  } else {
    float h = 1.0 - (d - R) / (1.0 - R);
    outc = vec4(vCol * uIntensity, vA * h * h * 0.28);
  }
  outc.rgb += vCol * star * 1.6 * uIntensity;
  outc.a = max(outc.a, clamp(star * vA * 1.5, 0.0, 1.0));
  gl_FragColor = outc;
}`,
    });

    const root = new THREE.Group();
    const points = new THREE.Points(geo, mat);
    root.add(points);

    const colA = new Hex();
    let angle = 0;

    return {
        root,
        update(t, dt, opt, w, speech) {
            uniforms.uTime.value = t;
            const intensity = clamp(opt.intensity ?? 1, 0.2, 2);
            uniforms.uIntensity.value = intensity;
            colA.set(opt.color ?? "#3B82F6").getHSL(hsl, THREE.SRGBColorSpace);
            uniforms.uHsl.value.set(hsl.h, Math.max(hsl.s, 0.55), clamp(hsl.l, 0.45, 0.65));
            uniforms.uW.value.set(w.idle, w.listen, w.talk, w.think);
            uniforms.uSpeech.value = speech * w.talk;

            angle += dt * (0.16 * w.idle + 0.45 * w.listen + (0.3 + 0.5 * speech) * w.talk + 1.2 * w.think);
            points.rotation.y = angle;
            points.rotation.x = angle * 0.67;
        },
        resize(height, pixelRatio) {
            uniforms.uPixel.value = pixelRatio * clamp(height / 600, 0.4, 2);
        },
    };
}

// ---------- Driply 3D ----------

/**
 * Driply: a single glowing plasma orb — bright grainy rim, ripples converging on a dark middle
 * with a hot pinpoint at the center, and a ghost of a second membrane just behind it.
 *  - idle      slow ripples, a faint breath
 *  - listening the orb opens up a little, the ghost membrane swells into an aura, rim glows brighter
 *  - talking   the whole orb pulses and its skin quivers with every syllable
 *  - thinking  it contracts, the ripples tighten into fine rings and the orb spins like a vortex
 */
function buildDriply(o: Symbol3DInteropOptions): Built {
    const root = new THREE.Group();
    const orb = plasmaShell({ radius: 1.75, amp: 0.015, freq: 1.0, seed: 1.3, ripple: 14, alpha: 1 });
    const ghost = plasmaShell({ radius: 1.88, amp: 0.025, freq: 0.8, seed: 5.1, ripple: 9, alpha: 0.12 });
    root.add(ghost, orb);

    const tex = glowTexture();
    const halo = glowSprite(tex);
    root.add(halo);

    // Magic motes: tiny lights lift off the orb like embers, drift up in lazy spirals and fade out.
    const MOTES = 60;
    const mpos = new Float32Array(MOTES * 3);
    const mcol = new Float32Array(MOTES * 3);
    const moteGeo = new THREE.BufferGeometry();
    moteGeo.setAttribute("position", new THREE.BufferAttribute(mpos, 3));
    moteGeo.setAttribute("color", new THREE.BufferAttribute(mcol, 3));
    const moteMat = new THREE.PointsMaterial({
        map: tex, size: 0.42, sizeAttenuation: true, transparent: true,
        depthWrite: false, blending: THREE.AdditiveBlending, vertexColors: true,
    });
    const motePts = new THREE.Points(moteGeo, moteMat);
    motePts.frustumCulled = false;
    root.add(motePts);
    const motes = Array.from({ length: MOTES }, () => ({ ang: 0, rad: 0, y: 0, vy: 0, age: 0, life: 1, spin: 0, mix: 0 }));
    const respawn = (m: typeof motes[number], fresh: boolean): void => {
        m.ang = rand(0, Math.PI * 2);
        m.rad = rand(1.95, 2.5);
        m.y = rand(-1.5, 0.4);
        m.vy = rand(0.18, 0.42);
        m.life = rand(3.5, 6.5);
        m.age = fresh ? rand(0, m.life) : 0;          // on first build, start spread out so it doesn't all begin at once
        m.spin = rand(0.15, 0.55) * (Math.random() < 0.5 ? -1 : 1);
        m.mix = Math.random();
    };
    motes.forEach((m) => respawn(m, true));
    const moteA = new THREE.Color();
    const moteB = new THREE.Color();
    const moteC = new THREE.Color();
    const moteWhite = new THREE.Color(1, 1, 1);

    // Magic pulses: now and then a ring of light sweeps across the orb from a random point.
    let pulseAge = -1;
    let pulseDir = new THREE.Vector3(0, 1, 0);

    const src = new Hex();
    const rimSrc = new Hex();
    let flow = 0;
    let spin = 0;
    const orbU = (orb.material as THREE.ShaderMaterial).uniforms;
    const ghostU = (ghost.material as THREE.ShaderMaterial).uniforms;
    return {
        root,
        update(t, dt, opt, w, speech) {
            const intensity = clamp(opt.intensity ?? 1, 0.2, 2);
            const distortion = opt.distortion ?? 1;
            const sp = speech * w.talk;
            flow += dt * (1 + 0.5 * w.listen + 1.2 * w.talk - 0.5 * w.think);
            spin += dt * (0.12 + 0.9 * w.think);

            const color = src.set(opt.color ?? "#3B82F6");
            const rim = rimSrc.set(opt.color2 ?? "#8B5CF6");   // the glowing edge takes the accent color
            setPlasmaPalette(orb, color, rim);
            setPlasmaPalette(ghost, color, rim);
            const glow = intensity * (1 + 0.25 * w.listen + 0.5 * sp);
            setPlasmaUniforms(orb, flow, glow, 0.015 * distortion * (1 + 3 * sp + 0.6 * w.listen));
            setPlasmaUniforms(ghost, flow, glow, 0.025 * distortion);
            orbU.uRipple.value = 14 * (1 + 0.7 * w.think + 0.2 * w.listen);
            ghostU.uAlpha.value = 0.12 + 0.22 * w.listen + 0.1 * sp;

            orb.rotation.y = spin;
            orb.rotation.x = 0.35 * w.think * Math.sin(t * 0.8);
            ghost.rotation.set(t * 0.07, -t * 0.09, 0);
            const pulse = 1 + 0.018 * Math.sin(t * 1.4);
            const size = 1 + 0.08 * w.listen + 0.1 * sp - 0.1 * w.think;
            orb.scale.setScalar(1.75 * pulse * size);
            ghost.scale.setScalar(1.88 * size * (1 + 0.12 * w.listen));

            const hm = halo.material as THREE.SpriteMaterial;
            hm.color.copy(orbU.uMid.value as THREE.Color);
            hm.opacity = clamp(0.2 * intensity * (1 + 0.6 * w.listen + 0.8 * sp) * (1 + 0.15 * Math.sin(t * 1.1)), 0, 0.7);
            halo.scale.setScalar(6.5);

            // pulses: rare while idle, steadier while listening, and on the peaks of the voice while talking
            if (pulseAge >= 0) {
                pulseAge += dt / 1.7;
                if (pulseAge >= 1) pulseAge = -1;
            } else {
                const rate = 0.12 * w.idle + 0.3 * w.listen + 0.25 * w.think + 1.4 * sp * w.talk;
                if (Math.random() < rate * dt) {
                    pulseAge = 0;
                    pulseDir = new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1)).normalize();
                }
            }
            for (const u of [orbU, ghostU]) {
                u.uPulseAge.value = pulseAge;
                (u.uPulseDir.value as THREE.Vector3).copy(pulseDir);
            }

            // motes: they spiral faster and tighter while thinking, lean in while listening, flare with the voice
            moteA.copy(orbU.uMid.value as THREE.Color).lerp(moteWhite, 0.35);
            moteB.copy(orbU.uHot.value as THREE.Color).lerp(moteWhite, 0.35);
            const lift = 1 + 0.8 * w.talk;
            const swirl = 1 + 2.5 * w.think + 0.5 * w.talk;
            const pull = 1 - 0.18 * w.think - 0.1 * w.listen;
            motes.forEach((m, i) => {
                m.age += dt;
                if (m.age >= m.life) respawn(m, false);
                m.ang += m.spin * dt * swirl;
                m.y += m.vy * dt * lift;
                const r = m.rad * pull + 0.15 * sp;
                mpos[i * 3] = Math.cos(m.ang) * r;
                mpos[i * 3 + 1] = m.y;
                mpos[i * 3 + 2] = Math.sin(m.ang) * r;
                const life = m.age / m.life;
                const fade = Math.pow(Math.sin(Math.PI * life), 1.3) * (0.6 + 0.4 * Math.sin(t * 4 + i * 1.7));
                moteC.copy(m.mix < 0.5 ? moteA : moteB).multiplyScalar(fade * intensity * 2.4 * (1 + 0.6 * sp));
                mcol[i * 3] = moteC.r;
                mcol[i * 3 + 1] = moteC.g;
                mcol[i * 3 + 2] = moteC.b;
            });
            (moteGeo.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
            (moteGeo.getAttribute("color") as THREE.BufferAttribute).needsUpdate = true;
        },
    };
}

// ---------- Orbitz 3D ----------

/**
 * Orbitz, like the CSS original: four soft, translucent, asymmetrical blobs, one per color, each
 * morphing and swinging back and forth on its own slow timing (5 / 18 / 23 / 31 s), plus a faint
 * twin of each swinging the other way. The big top blob hides most of the ones beneath it, so you
 * only ever see slivers of the lower layers sliding in and out. Here every blob is a lit 3D volume
 * whose edge fades out softly (the original's blur).
 *  - idle      the slow original drift
 *  - listening the blobs part and spread, opening more of the layers beneath; they drift a bit faster
 *  - talking   the blobs swing fast and swell with each syllable
 *  - thinking  they draw together into a compact knot that whirls
 */
const ORBITZ_FRAGMENT = /* glsl */ `
uniform vec3 uCol;
uniform float uAlpha;
uniform float uSoft;
uniform float uIntensity;
varying vec3 vN;
varying vec3 vV;
varying vec3 vD;
varying float vNoise;
void main() {
  vec3 N = normalize(vN);
  vec3 V = normalize(vV);
  float ndv = clamp(dot(N, V), 0.0, 1.0);
  vec3 L = normalize(vec3(-0.4, 0.7, 0.6));
  float diff = dot(N, L) * 0.5 + 0.5;
  float spec = pow(max(dot(N, normalize(L + V)), 0.0), 40.0) * 0.3;
  float rim = pow(1.0 - ndv, 2.5);
  vec3 col = uCol * (0.6 + 0.55 * diff * diff) + uCol * rim * 0.3 + spec;
  float a = uAlpha * smoothstep(0.0, uSoft, ndv);
  gl_FragColor = vec4(col * uIntensity, a);
${OUTPUT_GLSL}
}`;

function buildOrbitz(o: Symbol3DInteropOptions): Built {
    const root = new THREE.Group();

    interface Blob {
        mesh: THREE.Mesh;
        mat: THREE.ShaderMaterial;
        color: number;          // 0..3 index along the primary -> accent ramp
        amp: number;
        axis: THREE.Vector3;
        period: number;         // seconds for one swing, as in the original's animation durations
        swing: number;          // radians each way
        dir: number;
        home: THREE.Vector3;
        drift: THREE.Vector3;
        base: THREE.Vector3;    // resting scale
        ph: number;             // accumulated swing phase
    }
    const blobs: Blob[] = [];
    const geo = new THREE.IcosahedronGeometry(1, 36);

    const specs = [
        { color: 3, radius: 1.05, sq: [1.0, 0.9, 1.0], off: [0.0, 0.0, 0.0], period: 31, alpha: 0.75, soft: 0.9, seed: 6.1, amp: 0.3 },
        { color: 2, radius: 1.3, sq: [1.0, 0.85, 0.95], off: [-0.2, 0.18, 0.0], period: 23, alpha: 0.7, soft: 0.5, seed: 3.3, amp: 0.3 },
        { color: 1, radius: 1.4, sq: [0.92, 1.0, 0.95], off: [0.26, -0.14, 0.0], period: 18, alpha: 0.72, soft: 0.45, seed: 1.7, amp: 0.28 },
        { color: 0, radius: 1.5, sq: [1.0, 0.92, 0.96], off: [0.0, 0.0, 0.1], period: 5, alpha: 0.78, soft: 0.4, seed: 0.4, amp: 0.26 },
    ];
    let order = 0;
    for (const twin of [false, true]) {
        for (const s of specs) {
            const mat = new THREE.ShaderMaterial({
                uniforms: {
                    uTime: { value: 0 },
                    uAmp: { value: s.amp },
                    uSeed: { value: s.seed + (twin ? 9 : 0) },
                    uFreq: { value: 0.75 },
                    uAlpha: { value: twin ? s.alpha * 0.4 : s.alpha },
                    uSoft: { value: s.soft },
                    uIntensity: { value: 1 },
                    uCol: { value: new THREE.Color() },
                },
                transparent: true,
                depthWrite: false,
                vertexShader: surfaceVertex(/* glsl */ `
vec3 displace(vec3 d) {
  float n = snoise(d * uFreq + vec3(uSeed, uTime * 0.22, uSeed * 0.5)) * 0.7
          + snoise(d * uFreq * 1.9 - vec3(uTime * 0.18, uSeed, 0.0)) * 0.3;
  vNoise = n;
  return d * (1.0 + uAmp * n);
}`, "uniform float uAmp;\nuniform float uSeed;\nuniform float uFreq;"),
                fragmentShader: ORBITZ_FRAGMENT,
            });
            const mesh = new THREE.Mesh(geo, mat);
            const k = twin ? 0.95 : 1;
            const base = new THREE.Vector3(s.radius * s.sq[0] * k, s.radius * s.sq[1] * k, s.radius * s.sq[2] * k);
            mesh.scale.copy(base);
            // back to front: the smallest diffuse blob first, the big primary one on top, twins over all
            mesh.renderOrder = order++;
            root.add(mesh);
            blobs.push({
                mesh, mat, color: s.color, amp: s.amp, base, ph: rand(0, 6),
                axis: new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-0.3, 0.3)).normalize(),
                period: s.period, swing: rand(1.0, 1.6), dir: twin ? -1 : 1,
                home: new THREE.Vector3(...s.off),
                drift: new THREE.Vector3(rand(0.5, 1), rand(0.5, 1), 0),
            });
        }
    }

    const colA = new Hex();
    const colB = new Hex();
    const tint = new THREE.Color();
    const q = new THREE.Quaternion();
    let flow = 0;
    return {
        root,
        update(t, dt, opt, w, speech) {
            const intensity = clamp(opt.intensity ?? 1, 0.2, 2);
            const distortion = opt.distortion ?? 1;
            const sp = speech * w.talk;
            colA.set(opt.color ?? "#3B82F6");
            colB.set(opt.color2 ?? "#8B5CF6");

            // how fast the blobs swing, how far apart they sit, how big they are, how much they wobble
            const rate = 1 * w.idle + 1.4 * w.listen + (3 + 2 * speech) * w.talk + 2.4 * w.think;
            const spread = 1 + 0.9 * w.listen + 0.2 * w.talk - 0.95 * w.think;
            const size = 1 + 0.05 * w.listen + 0.16 * sp - 0.14 * w.think;
            const wobble = distortion * (1 + 0.4 * w.listen + 2 * sp + 0.3 * w.think);
            const swingK = 1 + 0.9 * w.think;
            flow += dt * (1 + 0.5 * w.listen + 1.5 * w.talk + 1.2 * w.think);

            for (const b of blobs) {
                const u = b.mat.uniforms;
                u.uTime.value = flow;
                u.uAmp.value = b.amp * wobble;
                u.uIntensity.value = intensity;
                (u.uCol.value as THREE.Color).copy(tint.copy(colA.c).lerpHSL(colB.c, b.color / 3));
                b.ph += (dt * rate / b.period) * Math.PI * 2;
                const ph = b.ph;
                // ease-in-out swing back and forth, like the original's `alternate ease-in-out`
                q.setFromAxisAngle(b.axis, Math.sin(ph) * b.swing * swingK * b.dir);
                b.mesh.quaternion.copy(q);
                b.mesh.position.set(
                    b.home.x * spread + Math.sin(ph * 1.7) * 0.08 * b.drift.x,
                    b.home.y * spread + Math.cos(ph * 1.3) * 0.08 * b.drift.y,
                    b.home.z,
                );
                b.mesh.scale.copy(b.base).multiplyScalar(size);
            }
        },
    };
}

// ---------- Crystal 3D ----------

const CORE_FRAGMENT = /* glsl */ `
${NOISE_GLSL}
uniform vec3 uA;
uniform vec3 uB;
uniform vec3 uHot;
uniform float uTime;
uniform float uWarp;
uniform float uIntensity;
varying vec3 vN;
varying vec3 vV;
varying vec3 vD;
varying float vNoise;
void main() {
  vec3 N = normalize(vN);
  vec3 V = normalize(vV);
  float ndv = clamp(dot(N, V), 0.0, 1.0);
  vec3 p = vD * 1.8;
  vec3 w = vec3(snoise(p * 0.8 + uTime * 0.2),
                snoise(p * 0.8 + vec3(5.2, 1.3, 2.8) - uTime * 0.17),
                snoise(p * 0.8 + vec3(9.1, 3.7, 6.4) + uTime * 0.13));
  float n = 0.5 + 0.5 * snoise(p * 0.9 + w * 1.6 * uWarp + uTime * 0.1);         // soft swirling body
  float vein = 1.0 - abs(snoise(p * 1.2 + w * 1.4 * uWarp - uTime * 0.15));      // thin glowing filaments
  vein = pow(vein, 7.0);
  vec3 col = mix(uA * 0.25, uB, smoothstep(0.25, 0.85, n));
  col += uHot * vein * 0.9;
  col += uA * pow(1.0 - ndv, 2.0) * 0.5;
  gl_FragColor = vec4(col * uIntensity, 1.0);
${OUTPUT_GLSL}
}`;

/**
 * Crystal 3D: a fully 3D glass sphere — real refraction, studio reflections, a faint rainbow
 * fringe — with a swirling liquid-light core, and suspended sparkles inside.
 *  - idle      slow swirl
 *  - listening the core brightens and the sparkles glow, like an eye widening
 *  - talking   the core beats with each syllable, its light floods the glass and the sparkles flare
 *  - thinking  the core churns hard and spins and the sparkles whirl around it
 */
function buildCrystal(o: Symbol3DInteropOptions): Built {
    const root = new THREE.Group();

    // glass shell
    const glass = new THREE.Mesh(
        new THREE.SphereGeometry(1.6, 128, 96),
        new THREE.MeshPhysicalMaterial({
            color: 0xffffff,
            metalness: 0,
            roughness: 0.03,
            transmission: 1,
            thickness: 1.6,
            ior: 1.4,
            dispersion: 0.25,
            iridescence: 0.12,
            iridescenceIOR: 1.25,
            iridescenceThicknessRange: [120, 420],
            clearcoat: 0.35,
            clearcoatRoughness: 0.06,
            specularIntensity: 0.45,
            specularColor: new THREE.Color(0xffffff),
            // thick glass at the rim absorbs the main color, so the border stays colored, not milky white
            attenuationDistance: 1.8,
            attenuationColor: new THREE.Color(0xffffff),
            envMapIntensity: 0.5,
        }),
    );
    glass.renderOrder = 2;

    const rimMat = new THREE.ShaderMaterial({
        uniforms: { uCol: { value: new THREE.Color() }, uAmount: { value: 1 } },
        transparent: true,
        depthWrite: false,
        vertexShader: /* glsl */ `
varying vec3 vN;
varying vec3 vV;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = -mv.xyz;
  gl_Position = projectionMatrix * mv;
}`,
        fragmentShader: /* glsl */ `
uniform vec3 uCol;
uniform float uAmount;
varying vec3 vN;
varying vec3 vV;
void main() {
  vec3 N = normalize(vN);
  float ndv = clamp(dot(N, normalize(vV)), 0.0, 1.0);
  float rimA = pow(1.0 - ndv, 2.4) * 0.9 * uAmount;        // opaque-ish at the silhouette, clear in the middle
  float down = clamp(0.5 - 0.5 * N.y, 0.0, 1.0);           // 1 at the bottom of the ball, 0 at the top
  float bodyA = down * down * 0.62 * uAmount;              // the lower half sits in shade, so it reads as a lit ball
  float a = max(rimA, bodyA);
  vec3 col = mix(uCol * 0.12, uCol, rimA / (rimA + bodyA + 1e-4));
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
${OUTPUT_GLSL}
}`,
    });
    const rim = new THREE.Mesh(new THREE.SphereGeometry(1.604, 96, 64), rimMat);
    rim.renderOrder = 4;

    // liquid-light core (the glass refracts it, so it shifts and magnifies as you tilt)
    const coreMat = new THREE.ShaderMaterial({
        uniforms: {
            uTime: { value: 0 },
            uWarp: { value: 1 },
            uIntensity: { value: 1 },
            uA: { value: new THREE.Color() },
            uB: { value: new THREE.Color() },
            uHot: { value: new THREE.Color() },
        },
        vertexShader: surfaceVertex(/* glsl */ `
vec3 displace(vec3 d) {
  vNoise = 0.0;
  return d * (1.0 + 0.03 * sin(uTime * 1.5 + d.y * 3.0));
}`, ""),
        fragmentShader: CORE_FRAGMENT,
    });
    const core = new THREE.Mesh(new THREE.IcosahedronGeometry(0.82, 24), coreMat);
    core.renderOrder = 1;

    // suspended sparkles
    const SPARKS = 140;
    const sp = new Float32Array(SPARKS * 3);
    for (let i = 0; i < SPARKS; i++) {
        const r = 0.95 + Math.random() * 0.5;
        const u = rand(-1, 1);
        const ph = rand(0, Math.PI * 2);
        const s = Math.sqrt(1 - u * u);
        sp.set([s * Math.cos(ph) * r, u * r, s * Math.sin(ph) * r], i * 3);
    }
    const sparkGeo = new THREE.BufferGeometry();
    sparkGeo.setAttribute("position", new THREE.BufferAttribute(sp, 3));
    const sparkCol = new Float32Array(SPARKS * 3);
    sparkGeo.setAttribute("color", new THREE.BufferAttribute(sparkCol, 3));
    const tex = glowTexture();
    const sparkMat = new THREE.PointsMaterial({
        map: tex, size: 0.11, sizeAttenuation: true, transparent: true,
        depthWrite: false, depthTest: false,   // the glass writes depth, so without this it would hide everything inside it
        blending: THREE.AdditiveBlending, vertexColors: true,
    });
    const sparks = new THREE.Points(sparkGeo, sparkMat);
    sparks.renderOrder = 3;

    // glow behind the sphere and a light inside it, so the rim picks up the core color
    const halo = glowSprite(tex);
    halo.scale.setScalar(7);
    halo.renderOrder = -1;
    const coreLight = new THREE.PointLight(0xffffff, 6, 5, 2);
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(-3, 4, 5);

    const floorGlow = glowSprite(tex);
    floorGlow.scale.set(5.2, 1.1, 1);
    floorGlow.position.set(0, -1.95, 0);
    floorGlow.renderOrder = -1;

    // Energy arcs: thin bolts of light flicker from the core out to the inside of the glass, like a plasma ball.
    const ARC_N = 24;
    interface Arc { line: THREE.Line; dots: THREE.Points; pos: Float32Array; age: number; life: number; dir: THREE.Vector3; side: THREE.Vector3 }
    const arcs: Arc[] = [];
    for (let i = 0; i < 4; i++) {
        const pos = new Float32Array(ARC_N * 3);
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
        const line = new THREE.Line(g, new THREE.LineBasicMaterial({ transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending }));
        const dots = new THREE.Points(g, new THREE.PointsMaterial({
            map: tex, size: 0.2, sizeAttenuation: true, transparent: true, depthWrite: false, depthTest: false, blending: THREE.AdditiveBlending,
        }));
        line.frustumCulled = dots.frustumCulled = false;
        line.visible = dots.visible = false;
        line.renderOrder = dots.renderOrder = 3;
        root.add(line, dots);
        arcs.push({ line, dots, pos, age: 99, life: 1, dir: new THREE.Vector3(), side: new THREE.Vector3() });
    }
    const arcA = new THREE.Vector3();
    const arcB = new THREE.Vector3();
    const arcJ = new THREE.Vector3();
    const arcRaw = new Float32Array(ARC_N * 3);

    root.add(halo, floorGlow, core, sparks, glass, rim, coreLight, key);

    const colA = new Hex();
    const colB = new Hex();
    const hot = new THREE.Color();
    const hsl = { h: 0, s: 0, l: 0 };
    const tint = new THREE.Color();
    const whiteC = new THREE.Color(1, 1, 1);
    let flow = 0;
    let coreSpin = 0;
    let sparkSpin = 0;
    return {
        root,
        update(t, dt, opt, w, speech) {
            const intensity = clamp(opt.intensity ?? 1, 0.2, 2);
            const sp = speech * w.talk;
            colA.set(opt.color ?? "#3B82F6");
            colB.set(opt.color2 ?? "#8B5CF6");
            flow += dt * (1 + 0.4 * w.talk + 0.9 * w.think);
            coreSpin += dt * (0.2 + 1.2 * w.think);
            sparkSpin += dt * (0.12 + 0.2 * w.talk + 0.8 * w.think);

            const u = coreMat.uniforms;
            u.uTime.value = flow;
            u.uWarp.value = (opt.distortion ?? 1) * (1 + 0.8 * w.think + 0.5 * sp);
            u.uIntensity.value = intensity * (1 + 0.3 * w.listen + 0.7 * sp + 0.2 * w.think);
            (u.uA.value as THREE.Color).copy(colA.c);
            (u.uB.value as THREE.Color).copy(colB.c);
            colA.c.getHSL(hsl, THREE.SRGBColorSpace);
            hot.setHSL((hsl.h + 0.97) % 1, hsl.s * 0.7, 0.82, THREE.SRGBColorSpace);
            (u.uHot.value as THREE.Color).copy(hot);

            const gm = glass.material as THREE.MeshPhysicalMaterial;
            (rimMat.uniforms.uCol.value as THREE.Color).copy(colA.c);
            gm.attenuationColor.copy(tint.copy(colA.c).lerp(whiteC, 0.3));
            gm.color.copy(tint.copy(colA.c).lerp(whiteC, 0.55));
            gm.specularColor.copy(tint.copy(colA.c).lerp(whiteC, 0.45));   // even the reflections lean toward the main color
            gm.envMapIntensity = 0.6 + 0.3 * intensity;
            glass.scale.setScalar(1 + 0.015 * sp);

            coreLight.color.copy(colA.c).lerp(colB.c, 0.3);
            coreLight.intensity = 6 * intensity * (1 + 0.5 * w.listen + 1.2 * sp);
            (halo.material as THREE.SpriteMaterial).color.copy(colA.c);
            (halo.material as THREE.SpriteMaterial).opacity = clamp(0.16 * intensity * (1 + 0.5 * sp), 0, 0.6);
            (floorGlow.material as THREE.SpriteMaterial).color.copy(colA.c);
            (floorGlow.material as THREE.SpriteMaterial).opacity = clamp(0.4 * intensity * (1 + 0.6 * sp), 0, 0.8);
            // twinkle: each sparkle brightens now and then on its own rhythm
            for (let i = 0; i < SPARKS; i++) {
                const tw = 0.3 + 0.7 * Math.pow(Math.max(0, Math.sin(t * (0.9 + (i % 7) * 0.35) + i * 1.7)), 6);
                sparkCol[i * 3] = hot.r * tw;
                sparkCol[i * 3 + 1] = hot.g * tw;
                sparkCol[i * 3 + 2] = hot.b * tw;
            }
            (sparkGeo.getAttribute("color") as THREE.BufferAttribute).needsUpdate = true;
            sparkMat.opacity = clamp(0.9 * intensity * (1 + 0.4 * w.listen + 0.8 * sp), 0, 1);
            sparkMat.size = 0.11 * (1 + 0.6 * sp);

            core.rotation.y = coreSpin;
            core.rotation.x = coreSpin * 0.55;
            core.scale.setScalar(1 + 0.2 * sp + 0.06 * w.listen - 0.1 * w.think);
            sparks.rotation.y = -sparkSpin;
            sparks.rotation.x = Math.sin(t * 0.2) * 0.2;

            // energy arcs: rare while idle, more while thinking, and on the voice while talking
            const arcRate = 0.7 * w.idle + 1.1 * w.listen + 3.5 * sp * w.talk + 2.4 * w.think;
            for (const arc of arcs) {
                if (arc.age >= arc.life) {
                    arc.line.visible = arc.dots.visible = false;
                    if (Math.random() < arcRate * dt) {
                        arc.age = 0;
                        arc.life = rand(0.22, 0.5);
                        arc.dir.set(rand(-1, 1), rand(-1, 1), rand(-0.3, 0.3)).normalize();   // mostly sideways, so the bolt crosses the view
                        arc.side.set(rand(-1, 1), rand(-1, 1), rand(-1, 1)).cross(arc.dir).normalize();
                        arc.line.visible = arc.dots.visible = true;
                    }
                    continue;
                }
                arc.age += dt;
                arcA.copy(arc.dir).multiplyScalar(0.78);
                arcB.copy(arc.dir).multiplyScalar(1.42).addScaledVector(arc.side, 0.3);
                for (let i = 0; i < ARC_N; i++) {
                    const fr = i / (ARC_N - 1);
                    arcJ.lerpVectors(arcA, arcB, fr);
                    arc.pos[i * 3] = arcJ.x;
                    arc.pos[i * 3 + 1] = arcJ.y;
                    arc.pos[i * 3 + 2] = arcJ.z;
                    const jit = 0.1 * Math.sin(Math.PI * fr);                 // wander in the middle, anchored at the ends
                    arcRaw[i * 3] = rand(-jit, jit);
                    arcRaw[i * 3 + 1] = rand(-jit, jit);
                    arcRaw[i * 3 + 2] = rand(-jit, jit);
                }
                for (let i = 1; i < ARC_N - 1; i++) {                          // smooth the wander so it reads as a bolt, not a scribble
                    for (let c = 0; c < 3; c++) {
                        arc.pos[i * 3 + c] += (arcRaw[(i - 1) * 3 + c] + arcRaw[i * 3 + c] + arcRaw[(i + 1) * 3 + c]) / 3;
                    }
                }
                (arc.line.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
                const a = (1 - arc.age / arc.life) * rand(0.6, 1) * intensity;
                const lm = arc.line.material as THREE.LineBasicMaterial;
                lm.color.copy(hot).multiplyScalar(a);
                const dm = arc.dots.material as THREE.PointsMaterial;
                dm.color.copy(hot).multiplyScalar(a * 0.8);
            }
        },
    };
}

// ---------- Glossy 3D ----------

/**
 * The water on the glossy sphere: rings spreading from drops that keep landing on the skin, over a slow
 * swell. Returns the height plus its analytic tangent-space gradient — analytic rather than a screen-space
 * difference, so the normals stay smooth and the crests do not break into 2x2-pixel steps.
 */
const WATER_GLSL = /* glsl */ `
uniform float uRipT, uRipAmp;
float waterHeight(vec3 p, out float crest, out vec3 grad) {
  float h = 0.0;
  crest = 0.0;
  grad = vec3(0.0);
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    // where the drop lands: fixed spots spread evenly over the sphere with a golden-angle spiral
    float yy = 1.0 - 2.0 * (fi + 0.5) / 8.0;
    float rr = sqrt(max(0.0, 1.0 - yy * yy));
    float ph = fi * 2.39996 + 0.7;
    vec3 c = vec3(cos(ph) * rr, yy, sin(ph) * rr);

    float period = 4.2 + fi * 0.85;
    float age = fract(uRipT / period + fi * 0.37);           // 0 = just landed, 1 = faded away
    float q = clamp(dot(p, c), -1.0, 1.0);
    float d = acos(q) - age * 2.4;                           // the ring travels outward over the sphere
    float env = exp(-d * d * 14.0) * (1.0 - age) * (1.0 - age);
    float wv = cos(d * 38.0);                                // fine, closely spaced crests
    h += wv * env;
    crest += max(wv, 0.0) * env;
    // d/dd of cos(38d) * env, times the gradient of the angle — the angle grows away from the drop point
    float dh = -env * (38.0 * sin(d * 38.0) + 28.0 * d * wv);
    vec3 away = (c - q * p) / max(0.08, sqrt(max(0.0, 1.0 - q * q)));   // guarded: the gradient blows up at a drop point
    grad -= dh * away;
  }

  // a slow swell, so the skin never sits still
  float a1 = p.x * 9.0 + uRipT * 0.9;
  float a2 = p.y * 7.5 - uRipT * 0.7;
  float a3 = p.z * 11.0 + p.x * 6.0 + uRipT * 0.6;
  h += 0.02 * sin(a1) * sin(a2) + 0.015 * sin(a3);
  grad += vec3(
    0.02 * 9.0 * cos(a1) * sin(a2) + 0.015 * 6.0 * cos(a3),
    0.02 * sin(a1) * 7.5 * cos(a2),
    0.015 * 11.0 * cos(a3));
  grad *= uRipAmp;
  return h * uRipAmp;
}
`;

/**
 * Glossy 3D: a coloured glass sphere — clear, sharply reflective glass around a primary→accent body,
 * with delicate water ripples running across its skin — and bright lights on tilted orbits around it.
 * The sphere hides the lights passing behind it, so the orbits really wrap around it in 3D.
 *  - idle      just the sphere, no lights
 *  - listening the lights fade in and shimmer now and then, each on its own rhythm
 *  - talking   the lights flash in time with the voice and the sphere swells a little with each syllable
 *  - thinking  a bright pulse chases around every ring like a loading spinner while the glass turns
 */
function buildGlossy(o: Symbol3DInteropOptions): Built {
    const root = new THREE.Group();
    const R = 1.3;

    // Glass shell. Transmission gives the real refraction and the hard studio reflections; the tint
    // lives in attenuationColor, so the colour deepens where light travels through the most glass
    // (the rim) and stays clear in the middle — that is what makes it read as coloured glass.
    const geo = new THREE.SphereGeometry(R, 160, 120);
    const mat = new THREE.MeshPhysicalMaterial({
        color: new THREE.Color(1, 1, 1),
        metalness: 0,
        roughness: 0.06,
        transmission: 1,
        thickness: R * 1.9,
        ior: 1.36,
        dispersion: 0.12,
        clearcoat: 0.15,
        clearcoatRoughness: 0.08,
        specularIntensity: 0.9,
        iridescence: 0.2,
        iridescenceIOR: 1.3,
        iridescenceThicknessRange: [150, 420],
        attenuationDistance: 1.1,
        attenuationColor: new THREE.Color(),
        envMapIntensity: 1.05,
    });
    const sphere = new THREE.Mesh(geo, mat);

    // Water. The shell bends its normals with these and the body paints the light they focus, so the same
    // rings travel across the skin and through the glass behind it.
    const water = {
        uRipT: { value: 0 },
        uRipAmp: { value: 1 },
        uRipBump: { value: 0.0038 },
        uRipCrest: { value: new THREE.Color() },
        uRipBasis: { value: new THREE.Matrix3() },   // the ball's rotation, so the ripples stay glued to its skin
    };

    // The coloured body just under the skin: a soft gradient the glass magnifies and the ripples bend,
    // so the colour seems to swim inside the glass rather than sit on top of it. It stays opaque so the
    // transmission pass can sample it through the shell.
    const bodyUniforms = {
        uA: { value: new THREE.Color() },
        uB: { value: new THREE.Color() },
        uTime: { value: 0 },
        uGlow: { value: 1 },
        uRipT: water.uRipT,
        uRipAmp: water.uRipAmp,
    };
    const bodyMat = new THREE.ShaderMaterial({
        uniforms: bodyUniforms,
        vertexShader: /* glsl */ `
varying vec3 vN;
varying vec3 vV;
varying vec3 vD;
void main() {
  vD = normalize(position);
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vN = normalize(normalMatrix * normal);
  vV = -mv.xyz;
  gl_Position = projectionMatrix * mv;
}`,
        fragmentShader: /* glsl */ `
uniform vec3 uA;
uniform vec3 uB;
uniform float uTime;
uniform float uGlow;
varying vec3 vN;
varying vec3 vV;
varying vec3 vD;
${WATER_GLSL}
void main() {
  vec3 N = normalize(vN);
  float ndv = clamp(dot(N, normalize(vV)), 0.0, 1.0);
  float band = clamp(0.5 + 0.5 * vD.y + 0.16 * sin(vD.x * 2.2 + vD.z * 1.6 + uTime * 0.25), 0.0, 1.0);
  vec3 col = mix(uB, uA, band);

  // the ripples focus the light inside the glass, so the rings read right across the ball and not only
  // where the skin happens to catch a reflection
  float crest;
  vec3 grad;
  float h = waterHeight(vD, crest, grad);
  col *= 1.0 + 0.3 * clamp(h, -1.0, 1.0);

  // a key light from the upper left: the bright-to-dark sweep across the ball is what makes it read as a
  // sphere, and the silhouette tucks away into shadow
  vec3 V = normalize(vV);
  vec3 L = normalize(vec3(-0.55, 0.62, 0.56));
  float diff = clamp(dot(N, L), 0.0, 1.0);
  float rim = 1.0 - ndv;
  float shade = 0.04 + 1.18 * pow(diff, 1.9);
  shade *= 1.0 - 0.55 * pow(rim, 2.5);
  col *= shade;

  // a tight highlight on the shoulder of the ball: the crisp spot is the strongest cue that this is a
  // shiny sphere, and it takes the two palette colours rather than white
  float spec = pow(clamp(dot(N, normalize(L + V)), 0.0, 1.0), 70.0);
  col += mix(uA, uB, 0.5) * spec * 1.9;
  col += mix(uA, uB, 0.5) * pow(rim, 8.0) * 0.55;   // a thin coloured edge keeps the silhouette on a dark page
  gl_FragColor = vec4(col * uGlow, 1.0);
${OUTPUT_GLSL}
}`,
    });
    const body = new THREE.Mesh(new THREE.SphereGeometry(R * 0.93, 96, 64), bodyMat);
    const ball = new THREE.Group();
    ball.add(body, sphere);

    // The shell bends its surface normal with the water field — and with it the refracted view and the
    // reflections — so the water reads as the glass itself moving, not as a separate layer.
    mat.onBeforeCompile = (shader) => {
        Object.assign(shader.uniforms, water);
        shader.vertexShader = shader.vertexShader
            .replace("#include <common>", "#include <common>\nvarying vec3 vObj;")
            .replace("#include <begin_vertex>", "#include <begin_vertex>\nvObj = position;");
        shader.fragmentShader = shader.fragmentShader
            .replace("#include <common>", "#include <common>\nvarying vec3 vObj;\nuniform float uRipBump;\nuniform vec3 uRipCrest;\nuniform mat3 uRipBasis;\n" + WATER_GLSL)
            .replace("#include <normal_fragment_maps>", `#include <normal_fragment_maps>
  float rippleCrest;
  vec3 rippleGrad;
  waterHeight(normalize(vObj), rippleCrest, rippleGrad);
  {
    float facing = clamp(dot(normal, normalize(vViewPosition)), 0.0, 1.0);
    // object space -> view space (the camera never rotates, so the ball's own matrix is enough)
    vec3 slope = (uRipBasis * rippleGrad) * uRipBump * smoothstep(0.15, 0.55, facing);
    slope *= min(1.0, 0.55 / max(length(slope), 1e-5));      // never let a spike flip the normal inside out
    normal = normalize(normal - slope);
    nonPerturbedNormal = normal;                              // let the clear coat ripple too
  }`)
            .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
  totalEmissiveRadiance += uRipCrest * min(rippleCrest * rippleCrest, 1.0) * uRipAmp * 0.12;   // a faint sheen on the crests`);
    };
    mat.customProgramCacheKey = () => "glossy-water";

    // orbiting lights: a few bright points on tilted rings. Each one stays dim for a long while,
    // then flashes up briefly with a little star glint, each on its own random rhythm.
    const RINGS = 6;
    const perRing = clamp(Math.round((o.particles ?? 400) / (RINGS * 8)), 3, 60);
    const tex = glowTexture();
    const lightUniforms = {
        uTime: { value: 0 },
        uSize: { value: 150 },
        uPixel: { value: 1 },
        uOpacity: { value: 1 },
        uListen: { value: 0 },
        uSpeech: { value: 0 },
        uThink: { value: 0 },
    };
    const dotMat = new THREE.ShaderMaterial({
        uniforms: lightUniforms,
        transparent: true,
        depthWrite: false,
        // premultiplied: the light adds onto a dark page, and its dark rim still reads on a light one
        blending: THREE.CustomBlending,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        vertexShader: /* glsl */ `
attribute vec3 aColor;
attribute vec3 aSeed;      // x: shimmer phase, y: shimmer rate, z: angle around the ring (0..1)
uniform float uTime, uSize, uPixel, uOpacity, uListen, uSpeech, uThink;
varying vec3 vCol;
varying float vShine;
varying float vPresence;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mv;
  float s = sin(uTime * aSeed.y + aSeed.x * 6.2831);
  float shine = pow(max(s, 0.0), 14.0);                     // brief flashes with long quiet gaps
  shine = max(shine, uSpeech * pow(max(sin(uTime * 6.0 * aSeed.y + aSeed.x * 40.0), 0.0), 4.0));   // flashes follow the voice
  shine = max(shine, uThink * pow(max(sin(aSeed.z * 6.2831 - uTime * 4.0), 0.0), 6.0));            // a pulse chases round each ring
  float base = 0.2 + 0.1 * sin(uTime * 0.7 + aSeed.x * 9.0);
  vShine = shine;
  vPresence = clamp((base + 2.4 * shine) * uOpacity * uListen, 0.0, 1.0);   // 0 while idle
  vCol = aColor * (base + 2.4 * shine) * uOpacity * uListen;               // lights only show when it is not idle
  gl_PointSize = uSize * uPixel / (-mv.z) * (0.55 + 0.9 * shine);
}`,
        fragmentShader: /* glsl */ `
varying vec3 vCol;
varying float vShine;
varying float vPresence;
void main() {
  vec2 p = gl_PointCoord - 0.5;
  float d = length(p);
  if (d > 0.5) discard;
  float k = d * 2.0;                                         // 0 at the centre, 1 at the edge of the sprite
  float glow = pow(max(1.0 - k, 0.0), 2.2);
  float core = smoothstep(0.16, 0.0, d);
  float star = (smoothstep(0.035, 0.0, abs(p.x)) * smoothstep(0.5, 0.0, abs(p.y))
              + smoothstep(0.035, 0.0, abs(p.y)) * smoothstep(0.5, 0.0, abs(p.x))) * vShine;

  // the head of the light keeps its palette colour at full strength even when the light is dim, so the
  // orbits stay readable on a light page; the bloom around it follows the actual brightness
  vec3 hue = vCol / max(length(vCol), 1e-4);
  float c = core * clamp(vPresence * 2.2, 0.0, 1.0);         // the bright head
  float g = glow * vPresence * 0.85;                         // its bloom
  float s = smoothstep(0.98, 0.42, k) * 0.4 * smoothstep(0.45, 0.85, vPresence);   // soft shadow, only around a flash

  // shadow, then bloom, then the head, composited back to front — that is what lets a light stay readable
  // on a light page, while it still reads as a glow on a dark one
  vec3 rgb = vec3(0.015, 0.015, 0.025) * s;
  float a = s;
  rgb = vCol * g + rgb * (1.0 - g);
  a = g + a * (1.0 - g);
  vec3 head = hue * (core * 1.9 + star * 1.6);
  rgb = head * c + rgb * (1.0 - c);
  a = c + a * (1.0 - c);
  gl_FragColor = vec4(rgb, a);
}`,
    });
    const rings: { group: THREE.Group; speed: number; colors: THREE.BufferAttribute; mix: Float32Array }[] = [];
    for (let r = 0; r < RINGS; r++) {
        const radius = 1.85 + (r % 3) * 0.24;
        const p = new Float32Array(perRing * 3);
        const seeds = new Float32Array(perRing * 3);
        const mixv = new Float32Array(perRing);
        for (let i = 0; i < perRing; i++) {
            const a = Math.random() * Math.PI * 2;
            const rr = radius + rand(-0.025, 0.025);
            p.set([Math.cos(a) * rr, rand(-0.02, 0.02), Math.sin(a) * rr], i * 3);
            seeds.set([Math.random(), rand(0.35, 1.1), a / (Math.PI * 2)], i * 3);
            mixv[i] = Math.random();
        }
        const g = new THREE.BufferGeometry();
        g.setAttribute("position", new THREE.BufferAttribute(p, 3));
        g.setAttribute("aSeed", new THREE.BufferAttribute(seeds, 3));
        const colors = new THREE.BufferAttribute(new Float32Array(perRing * 3), 3);
        g.setAttribute("aColor", colors);
        const pts = new THREE.Points(g, dotMat);
        pts.renderOrder = 2;
        const group = new THREE.Group();
        group.rotation.set(rand(-1.1, 1.1), rand(0, Math.PI), rand(-0.6, 0.6));
        group.add(pts);
        root.add(group);
        rings.push({ group, speed: (r % 2 === 0 ? 1 : -1) * rand(0.18, 0.5), colors, mix: mixv });
    }

    const halo = glowSprite(tex);
    halo.scale.setScalar(6.5);
    halo.renderOrder = -1;
    root.add(halo, ball);

    const colA = new Hex();
    const colB = new Hex();
    let lastA = "";
    let lastB = "";
    const cA = new THREE.Color();
    const cB = new THREE.Color();
    const cDot = new THREE.Color();
    const white = new THREE.Color(1, 1, 1);
    const ringAngle = rings.map(() => 0);
    let spin = 0;
    let rippleT = 0;
    return {
        root,
        update(t, dt, opt, w, speech) {
            const intensity = clamp(opt.intensity ?? 1, 0.2, 2);
            const sp = speech * w.talk;
            const hexA = opt.color ?? "#3B82F6";
            const hexB = opt.color2 ?? "#8B5CF6";
            colA.set(hexA);
            colB.set(hexB);

            if (hexA !== lastA || hexB !== lastB) {     // recolour the glass and the lights only when a colour changes
                lastA = hexA;
                lastB = hexB;
                cA.copy(colA.c);
                cB.copy(colB.c);
                for (const ring of rings) {
                    for (let i = 0; i < ring.mix.length; i++) {
                        const m = ring.mix[i];
                        cDot.copy(m < 0.45 ? cA : cB).lerp(white, 0.05 + 0.15 * Math.random());   // the two palette colours, never white
                        ring.colors.setXYZ(i, cDot.r, cDot.g, cDot.b);
                    }
                    ring.colors.needsUpdate = true;
                }
            }

            // the shell carries the tint (deepest at the rim), the body underneath supplies the gradient
            // that the glass magnifies, so the colour sits inside the glass instead of on its skin.
            // The reflections and the crest sheen take the accent colour, so nothing in the glass is white.
            mat.attenuationColor.copy(cA).lerp(white, 0.05);
            mat.color.copy(cA).lerp(cB, 0.5);
            mat.specularColor.copy(cB).lerp(white, 0.7);        // a hint of the accent in the highlight, but keep the glass bright
            mat.specularIntensity = 0.9;
            mat.iridescence = 0.16 + 0.12 * Math.sin(t * 0.5);
            mat.iridescenceThicknessRange = [160 + 90 * Math.sin(t * 0.4), 420 + 120 * Math.sin(t * 0.3 + 1)];
            mat.envMapIntensity = (1.05 + 0.4 * intensity) * (1 + 0.25 * sp);
            (bodyUniforms.uA.value as THREE.Color).copy(cA);
            (bodyUniforms.uB.value as THREE.Color).copy(cB);
            bodyUniforms.uTime.value = t;
            bodyUniforms.uGlow.value = (0.55 + 0.75 * intensity) * (1 + 0.45 * w.listen + 1.1 * sp);

            const lit = 1 - w.idle;                     // the lights only exist when it is not idle
            lightUniforms.uTime.value = t;
            lightUniforms.uOpacity.value = clamp(intensity, 0.2, 2);
            lightUniforms.uListen.value = lit;
            lightUniforms.uSpeech.value = sp;
            lightUniforms.uThink.value = w.think;
            // ripples: slow and calm while listening, big and fast with the voice, a steady quickened flow while thinking
            rippleT += dt * (1 - 0.6 * w.listen + 1.0 * w.talk + 1.5 * w.think);
            water.uRipT.value = rippleT;
            water.uRipAmp.value = 1 + 0.35 * w.listen + 2.2 * sp + 0.2 * w.think;
            water.uRipCrest.value.copy(cB).lerp(white, 0.15);     // the crest sheen glows in the accent colour
            (halo.material as THREE.SpriteMaterial).color.copy(colA.c);
            (halo.material as THREE.SpriteMaterial).opacity = clamp(0.14 * intensity * (1 + 0.6 * sp), 0, 0.5);

            spin += dt * (0.2 + 1.3 * w.think);
            ball.rotation.y = spin;
            ball.scale.setScalar(1 + 0.05 * sp + 0.012 * Math.sin(t * 1.5));
            water.uRipBasis.value.setFromMatrix4(ball.matrixWorld);   // keeps the ripples glued to the turning glass
            rings.forEach((ring, i) => {
                ring.group.visible = lit > 0.005;
                ringAngle[i] += dt * ring.speed * (1 + 2.5 * w.think + 0.6 * w.talk);
                ring.group.children[0].rotation.y = ringAngle[i];
            });
        },
        resize(height, pixelRatio) {
            lightUniforms.uPixel.value = pixelRatio * clamp(height / 600, 0.4, 2);
        },
    };
}

// ---------- mount management ----------

function build(variant: string, o: Symbol3DInteropOptions): Built {
    switch (variant) {
        case "driply-3d": return buildDriply(o);
        case "orbitz-3d": return buildOrbitz(o);
        case "crystal-3d": return buildCrystal(o);
        case "glossy-3d": return buildGlossy(o);
        default: return buildSparkly(o);
    }
}

/** Studio reflections + filmic tone mapping for the glass symbols; the others stay unlit/untouched. */
function setStudioLight(m: Mount3D, kind: "room" | "soft" | null, tint?: THREE.Color): void {
    const wanted = kind === "soft" && tint
        ? `soft:${Math.round(tint.r * 4)},${Math.round(tint.g * 4)},${Math.round(tint.b * 4)}`   // quantised, so a colour drag rebuilds the studio only a few times
        : kind ?? "";
    if (m.envKind !== wanted) {
        if (m.envRT) {
            m.scene.environment = null;
            m.envRT.dispose();
            m.envRT = undefined;
        }
        m.envKind = wanted;
        if (kind) {
            const pmrem = new THREE.PMREMGenerator(m.renderer);
            if (kind === "soft") {
                const studio = softStudioScene(tint ?? new THREE.Color(1, 1, 1));
                m.envRT = pmrem.fromScene(studio.scene, 0.02);
                studio.dispose();
            } else {
                const room = new RoomEnvironment();
                m.envRT = pmrem.fromScene(room, 0.04);
                room.dispose();
            }
            pmrem.dispose();
            m.scene.environment = m.envRT.texture;
        }
    }
    m.renderer.toneMapping = kind ? THREE.ACESFilmicToneMapping : THREE.NoToneMapping;
    m.renderer.toneMappingExposure = kind === "soft" ? 1.25 : kind ? 0.85 : 1;
}

/** The studio backdrop the current variant needs; the glossy glass takes its reflections from the accent colour. */
function syncStudioLight(m: Mount3D): void {
    const variant = m.options.variant;
    if (variant === "crystal-3d") setStudioLight(m, "room");
    else if (variant === "glossy-3d") setStudioLight(m, "soft", new THREE.Color(m.options.color2 ?? "#8B5CF6"));
    else setStudioLight(m, null);
}

function rebuild(m: Mount3D): void {
    if (m.built) {
        m.pivot.remove(m.built.root);
        disposeObject(m.built.root);
    }
    syncStudioLight(m);
    m.built = build(m.options.variant ?? "sparkly-3d", m.options);
    m.pivot.add(m.built.root);
    fit(m);
}

function fit(m: Mount3D): void {
    const w = Math.max(1, m.container.clientWidth);
    const h = Math.max(1, m.container.clientHeight);
    const pr = Math.min(window.devicePixelRatio || 1, 2);
    m.renderer.setPixelRatio(pr);
    m.renderer.setSize(w, h, false);
    m.renderer.domElement.style.width = "100%";
    m.renderer.domElement.style.height = "100%";
    m.camera.aspect = w / h;
    m.camera.position.z = CAMERA_Z * Math.max(1, 1 / m.camera.aspect);
    m.camera.updateProjectionMatrix();
    m.built?.resize?.(h, pr);
}

export function symbols3dInit(elementId: string, options: Symbol3DInteropOptions): void {
    symbols3dDispose(elementId);

    const container = document.getElementById(elementId);
    if (!container) return;

    try {
        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
        camera.position.set(0, 0, CAMERA_Z);
        const pivot = new THREE.Group();
        scene.add(pivot);
        container.appendChild(renderer.domElement);

        const mount: Mount3D = {
            renderer, scene, camera, pivot, container,
            observer: undefined as unknown as ResizeObserver,
            raf: 0,
            options: { ...options },
            time: 0,
            lastFrame: performance.now(),
            pointer: { x: 0, y: 0, active: false },
            tilt: { x: 0, y: 0 },
            w: { idle: 1, listen: 0, talk: 0, think: 0 },
            onMove: () => undefined,
            onLeave: () => undefined,
        };
        mount.onMove = (e: PointerEvent): void => {
            const r = container.getBoundingClientRect();
            mount.pointer.x = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1.5, 1.5);
            mount.pointer.y = clamp(-(((e.clientY - r.top) / r.height) * 2 - 1), -1.5, 1.5);
            mount.pointer.active = true;
        };
        mount.onLeave = (): void => { mount.pointer.active = false; };
        window.addEventListener("pointermove", mount.onMove, { passive: true });
        document.documentElement.addEventListener("mouseleave", mount.onLeave);

        renderer.setClearColor(new THREE.Color(options.bgColor ?? "#07070d"), 1);
        mount.observer = new ResizeObserver(() => fit(mount));
        mount.observer.observe(container);
        mounts.set(elementId, mount);
        rebuild(mount);

        const loop = (now: number): void => {
            mount.raf = requestAnimationFrame(loop);
            if (document.hidden) { mount.lastFrame = now; return; }
            const realDt = Math.min(0.05, (now - mount.lastFrame) / 1000);
            const o = mount.options;
            const dt = realDt * (o.speed ?? 1);
            mount.lastFrame = now;
            mount.time += dt;

            // tilt toward the cursor, or a slow idle sway when the pointer is away
            const strength = o.tilt ?? 1;
            const idle = mount.time;
            const tx = mount.pointer.active ? mount.pointer.x : Math.sin(idle * 0.35) * 0.35;
            const ty = mount.pointer.active ? mount.pointer.y : Math.sin(idle * 0.27 + 1.1) * 0.25;
            const k = 1 - Math.exp(-realDt * 4);
            mount.tilt.x += (tx - mount.tilt.x) * k;
            mount.tilt.y += (ty - mount.tilt.y) * k;
            mount.pivot.rotation.y = mount.tilt.x * 0.55 * strength;
            mount.pivot.rotation.x = -mount.tilt.y * 0.4 * strength;
            mount.pivot.scale.setScalar(clamp(o.scale ?? 1, 0.2, 3));

            // ease toward the current mode so every switch cross-fades (the four weights sum to 1)
            const mk = 1 - Math.exp(-realDt * 3);
            const target = MODES.indexOf((o.mode ?? "idle") as typeof MODES[number]);
            const w = mount.w;
            w.idle += ((target === 0 ? 1 : 0) - w.idle) * mk;
            w.listen += ((target === 1 ? 1 : 0) - w.listen) * mk;
            w.talk += ((target === 2 ? 1 : 0) - w.talk) * mk;
            w.think += ((target === 3 ? 1 : 0) - w.think) * mk;

            mount.built?.update(mount.time, dt, o, w, speechEnv(mount.time));
            renderer.render(scene, camera);
        };
        mount.raf = requestAnimationFrame(loop);
    } catch (error) {
        console.error("[Symbols3D] Failed to initialize:", error);
    }
}

export function symbols3dUpdate(elementId: string, options: Symbol3DInteropOptions): void {
    const mount = mounts.get(elementId);
    if (!mount) return;
    try {
        const prev = mount.options;
        const rebuildNeeded = (options.variant ?? "sparkly-3d") !== (prev.variant ?? "sparkly-3d")
            || (["sparkly-3d", "glossy-3d"].includes(options.variant ?? "sparkly-3d") && Math.round(options.particles ?? 400) !== Math.round(prev.particles ?? 400));
        mount.options = { ...options };
        mount.renderer.setClearColor(new THREE.Color(options.bgColor ?? "#07070d"), 1);
        syncStudioLight(mount);                 // a new accent colour retints the glass's reflections
        if (rebuildNeeded) rebuild(mount);
    } catch (error) {
        console.error("[Symbols3D] Failed to update:", error);
    }
}

export function symbols3dDispose(elementId: string): void {
    const mount = mounts.get(elementId);
    if (!mount) return;
    try {
        cancelAnimationFrame(mount.raf);
        mount.observer.disconnect();
        window.removeEventListener("pointermove", mount.onMove);
        document.documentElement.removeEventListener("mouseleave", mount.onLeave);
        if (mount.built) disposeObject(mount.built.root);
        mount.envRT?.dispose();
        mount.renderer.dispose();
        mount.renderer.forceContextLoss();
        mount.renderer.domElement.remove();
    } catch (error) {
        console.error("[Symbols3D] Failed to dispose:", error);
    }
    mounts.delete(elementId);
}

// app.ts (which creates window.App) may run before or after this module inside the same esbuild
// bundle depending on the order of the entry file's imports. Attach immediately when possible and
// always re-attach on the next tick, once the whole bundle has finished executing.
const attachToApp = (): void => {
    const w = window as any;
    const app = w.App ?? (w.App = {});
    app.symbols3dInit = symbols3dInit;
    app.symbols3dUpdate = symbols3dUpdate;
    app.symbols3dDispose = symbols3dDispose;
};

attachToApp();
setTimeout(attachToApp, 0);

export {};
